import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  type SubmissionReasonType,
  type VendorDetailsInterface,
  type VendorSubmissionPayloadType,
} from 'features/Enterprise/Vendor/types/VendorManagement'
import { toSubmissionShippingFromVendorDetails } from 'features/Enterprise/Vendor/utils/toSubmissionShippingFromVendorDetails'

export const SLICE_NAME = 'vendorReview'

const toSubmissionRelationList = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown[] }).items)) {
    return (value as { items: unknown[] }).items
  }
  return []
}

const toSubmissionRelationships = (
  relationships: VendorDetailsInterface['relationships'] | undefined,
): VendorSubmissionPayloadType['relationships'] => ({
  vendors: toSubmissionRelationList(relationships?.vendors) as [],
  brands: toSubmissionRelationList(relationships?.brands) as [],
})

interface AddEditVendorStateInterface {
  isLoading: boolean
  isReviewDrawerOpen: boolean
  vendorReviewId: number
  reviewDetails: VendorDetailsInterface
  vendorDetails: VendorDetailsInterface
  vendorName: string | null
  submissionReasons: SubmissionReasonType[]
  status: string
  approvalPic: string
  rejectedPic: string
  revisionCount: number
  vendorSubmissionPayload: VendorSubmissionPayloadType
  oldSubmissionReason: string
}

const initialState: AddEditVendorStateInterface = {
  isLoading: false,
  reviewDetails: {} as VendorDetailsInterface,
  vendorDetails: {} as VendorDetailsInterface,
  vendorName: null,
  isReviewDrawerOpen: false,
  vendorReviewId: 0,
  submissionReasons: [],
  status: '',
  approvalPic: '',
  rejectedPic: '',
  revisionCount: 0,
  vendorSubmissionPayload: {} as VendorSubmissionPayloadType,
  oldSubmissionReason: '',
}

const addEditVendorSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setIsReviewDrawerOpen: (
      state,
      action: PayloadAction<{ vendorReviewId: number; vendorName: string }>,
    ) => {
      state.isReviewDrawerOpen = true
      state.vendorReviewId = action.payload.vendorReviewId || 0
      state.vendorName = action.payload.vendorName || ''
    },
    setIsReviewDrawerClose: (state) => {
      state.isReviewDrawerOpen = false
      state.vendorReviewId = 0
      state.reviewDetails = {} as VendorDetailsInterface
      state.vendorDetails = {} as VendorDetailsInterface
      state.vendorName = null
    },
    setVendorReviewData: (
      state,
      action: PayloadAction<{
        submissionId: number | null
        reviewDetails: VendorDetailsInterface
        vendorDetails: VendorDetailsInterface
      }>,
    ) => {
      state.reviewDetails = action.payload.reviewDetails
      state.vendorDetails = action.payload.vendorDetails
    },
    setSubmissionReviewInfo: (
      state,
      action: PayloadAction<{
        reason: string
        reasons: SubmissionReasonType[]
        status: string
        approvalPic: string
        rejectedPic: string
        revisionCount: number
        id: number
      }>,
    ) => {
      state.oldSubmissionReason = action.payload.reason
      state.submissionReasons = action.payload.reasons
      state.status = action.payload.status
      state.approvalPic = action.payload.approvalPic
      state.rejectedPic = action.payload.rejectedPic
      state.revisionCount = action.payload.revisionCount
      state.vendorReviewId = action.payload.id
    },
    setVendorSubmissionPayload: (
      state,
      action: PayloadAction<{ data: VendorDetailsInterface; submission_id: number; user: string }>,
    ) => {
      const requestData = action.payload.data
      state.vendorSubmissionPayload = {
        vendor_id: requestData?.vendor_id || requestData?.id || 0,
        submission_id: action.payload.submission_id,
        name: requestData?.name,
        category: requestData?.category,
        vendor_type: requestData?.vendor_type,
        inventory_type: requestData?.inventory_type,
        currency_code: requestData?.currency_code,
        npwp: requestData?.npwp,
        credit_limit_nominal: requestData?.credit_limit_nominal ?? null,
        credit_limit_invoice: requestData?.credit_limit_invoice ?? null,
        credit_limit_source: requestData?.credit_limit_source ?? null,
        terms: requestData?.terms,
        tax_code: requestData?.tax_code,
        po_submission_schedules: requestData?.po_submission_schedules,
        cutoff_po_time: requestData?.cutoff_po_time,
        is_submit: true,
        sppkp_number: requestData?.sppkp_number,
        nib_number: requestData?.nib_number || '',
        companies: requestData?.companies,
        bank_accounts: requestData?.bank_accounts,
        general_contacts: requestData?.general_contacts,
        locations: requestData?.locations,
        astro_pic: requestData?.astro_pic,
        source_type: requestData?.source_type,
        relationships: toSubmissionRelationships(requestData?.relationships),
        forms: requestData?.forms || [],
        user: action?.payload?.user || '',
        shipping_information: toSubmissionShippingFromVendorDetails(
          requestData?.shipping_information,
        ),
        documents: requestData?.documents || [],
      } as unknown as VendorSubmissionPayloadType
    },
    setResetVendorReviewSlice: (state) => {
      state.reviewDetails = {} as VendorDetailsInterface
      state.vendorDetails = {} as VendorDetailsInterface
      state.vendorName = null
      state.isReviewDrawerOpen = false
      state.vendorReviewId = 0
      state.submissionReasons = []
      state.status = ''
      state.approvalPic = ''
      state.rejectedPic = ''
      state.revisionCount = 0
      state.vendorSubmissionPayload = {} as VendorSubmissionPayloadType
      state.oldSubmissionReason = ''
    },
  },
})

export const {
  setLoading,
  setIsReviewDrawerOpen,
  setIsReviewDrawerClose,
  setVendorReviewData,
  setSubmissionReviewInfo,
  setVendorSubmissionPayload,
  setResetVendorReviewSlice,
} = addEditVendorSlice.actions

export default addEditVendorSlice.reducer
